import { useEffect, useCallback, useState, useRef } from 'react';

import { Fn } from 'types/utils';

const CONNECTION_CHECK_ENDPOINT = `/favicon.ico`;

const useAppOnlineStatus = (onlineFn: Fn, offlineFn: Fn): void => {
  /**
   * Due to bug https://bugs.chromium.org/p/chromium/issues/detail?id=678075,
   * it's not reliable to detect if the browser is currently online or offline
   * based on `navigator.onLine`.
   * As a work around:
   * 1. We change the status upon `online` or `offline` events.
   * 2. We perform a HEAD request to /favicon.ico to verify connectivity after the online or offline event.
   * 3. We also check connectivity when the tab regains focus after being idle.
   */

  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const previousVisibilityState = useRef<'hidden' | 'visible' | null>(null);

  const checkNetworkConnection = useCallback(async () => {
    if (isCheckingConnection) return;

    try {
      setIsCheckingConnection(true);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(CONNECTION_CHECK_ENDPOINT, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        onlineFn();
      } else {
        offlineFn();
      }
    } catch (error) {
      offlineFn();
    } finally {
      setIsCheckingConnection(false);
    }
  }, [onlineFn, offlineFn, isCheckingConnection]);

  const handleOnlineStatus = useCallback(() => {
    checkNetworkConnection();
  }, [checkNetworkConnection]);

  const handleVisibilityChange = useCallback(() => {
    const currentVisibility = document.visibilityState;

    if (currentVisibility === 'visible' && previousVisibilityState.current === 'hidden') {
      checkNetworkConnection();
    }

    previousVisibilityState.current = currentVisibility;
  }, [checkNetworkConnection]);

  useEffect(() => {
    previousVisibilityState.current = document.visibilityState;

    window?.addEventListener('online', handleOnlineStatus);
    window?.addEventListener('offline', handleOnlineStatus);
    document?.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window?.removeEventListener('online', handleOnlineStatus);
      window?.removeEventListener('offline', handleOnlineStatus);
      document?.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleOnlineStatus, handleVisibilityChange]);
};

export default useAppOnlineStatus;
